/**
 * @file WelcomeCard.tsx
 * @description One-time onboarding card on the customer dashboard.
 *   Surfaces a short pitch ("here's how this portal works") with a CTA
 *   to the full /help guide and a "got it" button that dismisses the
 *   card permanently. Dismiss state lives in localStorage so it survives
 *   sign-outs and respects per-browser privacy.
 * @module Components
 * @security No PII rendered. localStorage key is namespaced (versioned)
 *   so future updates can re-show the card if the content changes.
 */

import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY = 'welcome_dismissed_v1';

/** True once the user has clicked "don't show again". Checked at mount
 *  so a sign-in that loads the dashboard freshly hits this once.
 *  localStorage may throw in private-window edge cases — fall back to
 *  showing the card; better noisy than missed for a first-timer. */
function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function WelcomeCard(): JSX.Element | null {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(true);

  // Defer the read to mount so SSR / hydration is consistent — we render
  // null on the first pass and the real card after the effect fires.
  useEffect(() => setDismissed(readDismissed()), []);

  if (dismissed) return null;

  const onDismiss = (): void => {
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* private mode — accept the noisy refresh next time */
    }
    setDismissed(true);
  };

  return (
    <section
      role="region"
      aria-label={t('welcome.aria')}
      className="rounded-xl border border-brand/30 bg-brand-soft/40 p-4 space-y-3 shadow-card"
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="font-semibold text-base text-slate-900">{t('welcome.title')}</h2>
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('welcome.dismiss_aria')}
          className="text-slate-500 hover:text-slate-800 rounded-md p-1.5 -mt-1 -me-1 hover:bg-slate-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4">
            <path
              d="M5 5l10 10M15 5L5 15"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
      <p className="text-sm text-slate-700 leading-relaxed">{t('welcome.body')}</p>
      <ul className="text-sm text-slate-700 list-disc ms-5 space-y-1">
        <li>{t('welcome.point_projects')}</li>
        <li>{t('welcome.point_quotes')}</li>
        <li>{t('welcome.point_chat')}</li>
        <li>{t('welcome.point_files')}</li>
      </ul>
      <div className="flex flex-wrap gap-3 pt-1">
        <Link
          to="/help"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand text-brand-fg text-sm font-medium shadow-sm hover:bg-brand-strong transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
        >
          {t('welcome.cta_guide')}
        </Link>
        <button
          type="button"
          onClick={onDismiss}
          className="text-sm text-slate-600 hover:underline self-center rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          {t('welcome.cta_dismiss')}
        </button>
      </div>
    </section>
  );
}
